/**
 * «کِی؟»ِ یه رخداد — یه مدلِ واحد برای هر سه چیزی که یه رخداد می‌تونه بگه:
 *
 *   یه تاریخ            پنجشنبه ۵ مرداد، ۲۰:۰۰ تا ۲۳:۰۰
 *   یه بازه             ۵ تا ۸ مرداد، هر روز ۲۰:۰۰ تا ۲۳:۰۰
 *   چند تاریخ           ۵ مرداد، ۱۲ مرداد، ۲۶ مرداد…
 *   تکرار               هر پنجشنبه، یا اولین جمعه‌ی هر ماه، یا روزِ ۱ هر ماه
 *
 * چرا یه مدلِ واحد و نه سه تا فرمِ جدا: توی دیتابیس این‌ها سه جای متفاوتن
 * (ستون‌های خودِ `events`، جدولِ `event_occurrences`، و ستون‌های الگو)، ولی
 * برای کسی که فرم رو پر می‌کنه همه‌شون یه سوالن. `toDraft` تهِ کار دوباره
 * پخششون می‌کنه بینِ همون سه جا.
 *
 * برخلافِ سیگنال، ساعت این‌جا اختیاری نیست: رخداد بدونِ «از کِی تا کِی» به
 * دردِ برنامه‌ریزیِ روزِ کسی که می‌خواد بره نمی‌خوره.
 */
import { dayOf, rangeTap, type Clock } from './dateChoice.ts';
import type { OccurrenceDraft } from '../domain/types.ts';

export type { Clock };

/** یه تاریخ: یه روز (یا بازه‌ای از روزها)، با ساعتِ شروع و پایان. */
export interface EventDate {
  /** روزِ شروع، نیمه‌شبِ محلی. */
  start: Date | null;
  /** روزِ پایانِ بازه. خالی یعنی همون یه روز. */
  end: Date | null;
  startTime: Clock;
  endTime: Clock;
  /**
   * چیزهایی که این فرم ویرایش نمی‌کنه ولی نباید گمشون کنه.
   *
   * یه «اجرا» می‌تونه عنوان و لینک و مکانِ خودش رو داشته باشه (بازیِ دو تیم،
   * سانسِ یه سالنِ دیگه — مایگریشن 0054). فرم فقط تاریخش رو دست می‌زنه، پس
   * بقیه دست‌نخورده این‌جا سوار می‌شن و همون‌طور برمی‌گردن.
   */
  keep?: Omit<OccurrenceDraft, 'startsAt' | 'endsAt'>;
}

export interface EventDates {
  /** همه‌ی تاریخ‌ها. **اولی** تاریخِ خودِ رخداده، بقیه «اجرا»ن. */
  dates: EventDate[];
  /** ۰=شنبه … ۶=جمعه. */
  weekdays: number[];
  /** روزهای ماه، ۱..۳۱. */
  monthdays: number[];
  /** هفته‌ی چندمِ ماه (۱..۵، ‎-1=آخرین) — با `weekdays` معنی می‌ده. */
  monthWeeks: number[];
}

/** ساعتِ پیش‌فرضِ یه تاریخِ تازه. عصر، چون بیشترِ رخدادها عصرن. */
export const DEFAULT_START: Clock = { h: 19, m: 0 };
export const DEFAULT_END: Clock = { h: 21, m: 0 };

export const EMPTY_DATES: EventDates = {
  dates: [], weekdays: [], monthdays: [], monthWeeks: [],
};

/** یه تاریخِ خالیِ تازه — بدونِ روز، با ساعتِ پیش‌فرض. */
export const newDate = (): EventDate => ({
  start: null, end: null, startTime: { ...DEFAULT_START }, endTime: { ...DEFAULT_END },
});

/** لمسِ یه روز روی تقویم، برای تاریخِ iاُم. قانونش همونِ سیگناله. */
export function tapDay(v: EventDates, i: number, d: Date): EventDates {
  return { ...v, dates: v.dates.map((x, j) => (i === j ? rangeTap(x, d) : x)) };
}

/** ساعتِ شروع یا پایانِ تاریخِ iاُم رو عوض می‌کنه. */
export function setClock(
  v: EventDates, i: number, which: 'startTime' | 'endTime', c: Clock,
): EventDates {
  return { ...v, dates: v.dates.map((x, j) => (i === j ? { ...x, [which]: c } : x)) };
}

const at = (day: Date, c: Clock): Date => {
  const d = dayOf(day);
  d.setHours(c.h, c.m, 0, 0);
  return d;
};

/**
 * دو لحظه‌ی واقعیِ یه تاریخ. `null` یعنی هنوز روزی انتخاب نشده.
 *
 * پایان روی **روزِ آخرِ بازه** می‌شینه، نه روزِ اول: «۵ تا ۸ مرداد، ۲۰ تا ۲۳»
 * یعنی چیزی که هشتم شب تموم می‌شه.
 *
 * ساعتِ پایانی که از شروع کمتره یعنی رخدادِ بعد از نیمه‌شب (۲۲:۰۰ تا ۰۱:۰۰)،
 * پس یه روز جلو می‌ره — وگرنه پایانِ زودتر از شروع ذخیره می‌شد.
 */
export function instantsOf(d: EventDate): { startsAt: Date; endsAt: Date } | null {
  if (!d.start) return null;
  const startsAt = at(d.start, d.startTime);
  const endDay = d.end ?? d.start;
  let endsAt = at(endDay, d.endTime);
  if (endsAt.getTime() <= startsAt.getTime()) {
    endsAt = at(new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() + 1), d.endTime);
  }
  return { startsAt, endsAt };
}

/** تاریخ‌هایی که واقعاً روز دارن. بقیه سطرِ نیمه‌کاره‌ن و ذخیره نمی‌شن. */
const filled = (v: EventDates): EventDate[] => v.dates.filter((d) => !!d.start);

/** چیزی برای ذخیره هست؟ یه الگوی تکرار به‌تنهایی هم کافیه. */
export const hasWhen = (v: EventDates): boolean =>
  filled(v).length > 0 || v.weekdays.length > 0 || v.monthdays.length > 0;

/**
 * مدلِ فرم → چیزی که ذخیره می‌شه.
 *
 * اولین تاریخ روی خودِ ردیفِ رخداد می‌شینه (`starts_at`/`ends_at`) و بقیه
 * می‌شن «اجرا» — همون قراری که ۹۰٪ رخدادها هیچ سطرِ اجرایی نداشته باشن.
 */
export function toDraft(v: EventDates): {
  startsAt?: Date;
  endsAt?: Date;
  occurrences: OccurrenceDraft[];
  weekdays: number[];
  monthdays: number[];
  monthWeeks: number[];
} {
  const rows = filled(v).map((d) => ({ d, t: instantsOf(d)! }));
  const [first, ...rest] = rows;
  return {
    startsAt: first?.t.startsAt,
    endsAt: first?.t.endsAt,
    occurrences: rest.map((r) => ({ ...r.d.keep, startsAt: r.t.startsAt, endsAt: r.t.endsAt })),
    weekdays: v.weekdays,
    monthdays: v.monthdays,
    monthWeeks: v.monthWeeks,
  };
}

const clockOf = (d: Date): Clock => ({ h: d.getHours(), m: d.getMinutes() });

/**
 * ساعتِ پایانِ حدسی برای رخدادی که فقط شروع داره (بیشترِ رخدادهای کرال‌شده).
 *
 * دو ساعت بعدِ **شروع**، نه یه ساعتِ ثابت: رخدادی که ۲۲:۰۰ شروع می‌شه با
 * پیش‌فرضِ ۲۱:۰۰ یه رخدادِ ۲۳ ساعته می‌شد.
 */
const twoHoursAfter = (c: Clock): Clock => ({ h: (c.h + 2) % 24, m: c.m });

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * یه لحظه‌ی شروع و یه لحظه‌ی پایان → یه تاریخِ فرم.
 *
 * بازه بودن با **طول** تشخیص داده می‌شه نه با شماره‌ی روز: کنسرتی که ۲۲:۰۰
 * شروع می‌شه و ۰۱:۰۰ تموم، دو تا تاریخِ تقویمی داره ولی یه شبه. بیشتر از
 * یه شبانه‌روز یعنی واقعاً چند روزه.
 */
function dateFrom(startsAt: Date, endsAt?: Date | null, keep?: EventDate['keep']): EventDate {
  const multiDay = !!endsAt && endsAt.getTime() - startsAt.getTime() > DAY_MS;
  return {
    start: dayOf(startsAt),
    end: multiDay && endsAt ? dayOf(endsAt) : null,
    startTime: clockOf(startsAt),
    endTime: endsAt ? clockOf(endsAt) : twoHoursAfter(clockOf(startsAt)),
    keep,
  };
}

/** چیزی که ذخیره شده → مدلِ فرم. */
export function fromEvent(
  e: {
    startsAt?: string | null; endsAt?: string | null;
    weekdays?: number[] | null; monthdays?: number[] | null; monthWeeks?: number[] | null;
  },
  occurrences: OccurrenceDraft[] = [],
): EventDates {
  const dates: EventDate[] = [];
  if (e.startsAt) dates.push(dateFrom(new Date(e.startsAt), e.endsAt ? new Date(e.endsAt) : null));
  for (const o of occurrences) {
    const { startsAt, endsAt, ...keep } = o;
    dates.push(dateFrom(startsAt, endsAt, keep));
  }
  return {
    dates,
    weekdays: e.weekdays ?? [],
    monthdays: e.monthdays ?? [],
    monthWeeks: e.monthWeeks ?? [],
  };
}
